import { useCallback, useEffect, useRef, useState } from "react";
import type { RadarStateSource } from "@radar-sim/engine";
import type { LatLon, TargetState, WorldSnapshot } from "@radar-sim/schema";
import type { AreaData, Mark } from "@radar-sim/area-data";
import {
  getTidalAnomalies,
  isClosedRing,
  lightFrame,
  mainlandLandSide,
} from "@radar-sim/area-data";
import type { DisplayState } from "../ppi/displayState.js";
import type { MarpaTracker } from "../ppi/marpa.js";
import { describeLight } from "@radar-sim/area-data";
import { describeDayShape, dayShapeFor, lightsFor, fogSignalFor } from "../identification/vesselIdentity.js";
import { DayShapeIcon } from "../identification/DayShapeIcon.js";
import { LightArrangementDiagram } from "../identification/LightArrangementDiagram.js";

/**
 * OpenCPN-style chart side panel. Renders the area's coastline, marks
 * and ferry lanes alongside the live snapshot's own ship, tracked
 * vessels, and active SART transponders. The radar PPI is the
 * student's primary instrument; this panel is the chart-table view
 * they'd glance at to cross-check what they're plotting.
 *
 * Visual style is deliberately understated and chart-like (sea blue,
 * tan land, black coastline) rather than radar-green so the two
 * views read as different kinds of data — radar is what the rotating
 * antenna sees right now; chart is the static reference picture.
 */
export interface ChartViewProps {
  source: RadarStateSource;
  area: AreaData | undefined;
  /** Display state from the PPI — the chart inherits the radar's range
   *  scale so zooming the radar zooms the chart in lockstep. */
  display: DisplayState;
  onDisplayChange: (next: DisplayState) => void;
  /** Optional MARPA tracker — lets a tap on a vessel acquire it the
   *  same way as on the PPI. Omit in classroom/replay views. */
  marpa?: MarpaTracker;
  /** When non-null, the next chart click resolves a pending vessel
   *  spawn at the tapped location instead of acquiring a target. */
  onPendingSpawnAt?: (at: LatLon) => void;
}

const COLOURS = {
  sea: "#cfe5f1",
  seaDeep: "#a9cbe0",
  land: "#f1e6c8",
  landEdge: "#7a6a4a",
  contour: "rgba(60, 110, 160, 0.4)",
  laneFerry: "rgba(170, 110, 180, 0.55)",
  buoy: "#1f5b8d",
  tower: "#0e2a40",
  raconHalo: "#e0a020",
  ownShip: "#0e7c3a",
  ownVector: "rgba(14, 124, 58, 0.65)",
  target: "#222",
  sart: "#d33",
  grid: "rgba(40, 80, 120, 0.18)",
} as const;

export function ChartView(props: ChartViewProps): JSX.Element {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [size, setSize] = useState({ w: 320, h: 320 });
  const [snapshot, setSnapshot] = useState<WorldSnapshot | null>(null);
  const [selectedMark, setSelectedMark] = useState<{
    mark: Mark;
    x: number;
    y: number;
  } | null>(null);
  const [selectedTarget, setSelectedTarget] = useState<{
    target: TargetState;
    x: number;
    y: number;
  } | null>(null);

  useEffect(() => {
    const wrap = wrapperRef.current;
    if (wrap === null) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) {
        const { width, height } = e.contentRect;
        setSize({ w: Math.max(120, Math.floor(width)), h: Math.max(120, Math.floor(height)) });
      }
    });
    ro.observe(wrap);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const stop = props.source.subscribe((s) => setSnapshot(s));
    return stop;
  }, [props.source]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas === null) return;
    const ctx = canvas.getContext("2d");
    if (ctx === null) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.floor(size.w * dpr);
    canvas.height = Math.floor(size.h * dpr);
    canvas.style.width = `${size.w}px`;
    canvas.style.height = `${size.h}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    drawChart(ctx, size, props.area, snapshot, props.display);
  }, [size, props.area, snapshot, props.display]);

  const handleTap = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      const area = props.area;
      if (canvas === null || area === undefined || snapshot === null) return;
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const upDeg =
        props.display.chartOrientation === "head-up"
          ? snapshot.ownShip.cog
          : 0;
      const proj = makeProjection(
        snapshot.ownShip.position,
        props.display.rangeNm,
        size,
        upDeg,
      );
      // If a vessel-spawn is pending (the user picked a vessel type
      // in click-to-place mode), the next tap fulfils that spawn at
      // the chosen world point — skip the normal mark/target
      // hit-testing entirely.
      if (props.onPendingSpawnAt !== undefined) {
        const at = proj.fromXY({ x, y });
        props.onPendingSpawnAt(at);
        return;
      }
      // Hit-test marks first (smaller, shouldn't be eclipsed by
      // vessels). 14 px hit radius — chart symbols are tiny.
      let bestMark: Mark | undefined;
      let bestMarkDist = 14;
      for (const m of area.marks) {
        const xy = proj.toXY(m.position);
        const d = Math.hypot(xy.x - x, xy.y - y);
        if (d < bestMarkDist) {
          bestMarkDist = d;
          bestMark = m;
        }
      }
      if (bestMark !== undefined) {
        const xy = proj.toXY(bestMark.position);
        setSelectedMark({ mark: bestMark, x: xy.x, y: xy.y });
        setSelectedTarget(null);
        return;
      }
      // Then vessels.
      let bestTarget: TargetState | undefined;
      let bestTargetDist = 16;
      for (const t of snapshot.targets) {
        if (!t.visible) continue;
        const xy = proj.toXY(t.position);
        const d = Math.hypot(xy.x - x, xy.y - y);
        if (d < bestTargetDist) {
          bestTargetDist = d;
          bestTarget = t;
        }
      }
      if (bestTarget !== undefined) {
        const xy = proj.toXY(bestTarget.position);
        setSelectedTarget({ target: bestTarget, x: xy.x, y: xy.y });
        setSelectedMark(null);
        if (props.marpa !== undefined) {
          if (props.marpa.isAcquired(bestTarget.id)) {
            props.marpa.drop(bestTarget.id);
          } else {
            props.marpa.acquire(bestTarget.id, snapshot.simTimeSeconds);
            void props.source.sendCommand({
              kind: "acquire_target",
              vesselId: bestTarget.id,
            });
          }
        }
        return;
      }
      setSelectedMark(null);
      setSelectedTarget(null);
    },
    [
      props.area,
      props.display.chartOrientation,
      props.display.rangeNm,
      props.source,
      props.marpa,
      props.onPendingSpawnAt,
      snapshot,
      size,
    ],
  );

  // Update popover anchor as own ship moves.
  const popoverPos = (latLon: LatLon): { x: number; y: number } => {
    if (snapshot === null) return { x: 0, y: 0 };
    const upDeg =
      props.display.chartOrientation === "head-up" ? snapshot.ownShip.cog : 0;
    const proj = makeProjection(
      snapshot.ownShip.position,
      props.display.rangeNm,
      size,
      upDeg,
    );
    return proj.toXY(latLon);
  };
  const markAnchor =
    selectedMark !== null ? popoverPos(selectedMark.mark.position) : null;
  const targetAnchor =
    selectedTarget !== null
      ? popoverPos(selectedTarget.target.position)
      : null;

  return (
    <div
      ref={wrapperRef}
      style={{
        width: "100%",
        height: "100%",
        background: COLOURS.sea,
        border: "1px solid var(--border)",
        borderRadius: "var(--radius-sm)",
        overflow: "hidden",
        position: "relative",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <ChartToolbar
        display={props.display}
        onChange={props.onDisplayChange}
      />
      <div style={{ position: "relative", flex: 1, minHeight: 0 }}>
        <canvas
          ref={canvasRef}
          onPointerDown={handleTap}
          style={{ display: "block", touchAction: "none" }}
          aria-label="Chart view"
        />
        {selectedMark !== null && markAnchor !== null && (
          <ChartMarkPopover
            mark={selectedMark.mark}
            x={markAnchor.x}
            y={markAnchor.y}
            containerSize={size}
            onClose={() => setSelectedMark(null)}
          />
        )}
        {selectedTarget !== null && targetAnchor !== null && (
          <ChartTargetPopover
            target={selectedTarget.target}
            x={targetAnchor.x}
            y={targetAnchor.y}
            containerSize={size}
            onClose={() => setSelectedTarget(null)}
          />
        )}
        {props.area === undefined ? (
          <div style={overlayStyle}>No chart data for this area</div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Range scales available when the chart is driving zoom on its own —
 * matches the radar's range-scale set so toggling radar on/off keeps
 * the same step granularity. */
const CHART_RANGE_SCALES_NM = [
  0.125, 0.25, 0.5, 0.75, 1.5, 3, 6, 12, 24, 48, 72,
] as const;

function ChartToolbar(props: {
  display: DisplayState;
  onChange: (next: DisplayState) => void;
}): JSX.Element {
  const { display, onChange } = props;
  // Step through chart range scales (only used when radar is off —
  // when radar is on, the radar's own range buttons drive zoom).
  const stepRange = (delta: 1 | -1) => {
    const idx = CHART_RANGE_SCALES_NM.findIndex(
      (r) => Math.abs(r - display.rangeNm) < 1e-6,
    );
    const fallback = CHART_RANGE_SCALES_NM.findIndex((r) => r >= display.rangeNm);
    const cur = idx >= 0 ? idx : fallback >= 0 ? fallback : 0;
    const next = Math.max(
      0,
      Math.min(CHART_RANGE_SCALES_NM.length - 1, cur + delta),
    );
    onChange({ ...display, rangeNm: CHART_RANGE_SCALES_NM[next]! });
  };
  return (
    <div
      style={{
        display: "flex",
        gap: 4,
        padding: "4px 6px",
        background: "rgba(11, 18, 32, 0.78)",
        borderBottom: "1px solid var(--border)",
        flexShrink: 0,
        alignItems: "center",
      }}
    >
      <ToolbarButton
        active={display.chartOrientation === "head-up"}
        onClick={() =>
          onChange({
            ...display,
            chartOrientation:
              display.chartOrientation === "head-up" ? "north-up" : "head-up",
          })
        }
        title="Toggle north-up / head-up chart orientation"
      >
        {display.chartOrientation === "head-up" ? "H↑" : "N↑"}
      </ToolbarButton>
      <ToolbarButton
        active={display.chartVesselPaint === "ais"}
        onClick={() =>
          onChange({
            ...display,
            chartVesselPaint:
              display.chartVesselPaint === "ais" ? "silhouette" : "ais",
          })
        }
        title="Toggle AIS triangles vs hull silhouettes"
      >
        AIS
      </ToolbarButton>
      {/* Chart zoom — only visible when the radar is off, since the
          radar's own range-scale buttons normally drive this. */}
      {!display.radarVisible && (
        <>
          <span
            style={{
              fontSize: 10,
              color: "rgba(255,255,255,0.55)",
              marginLeft: 6,
            }}
          >
            ZOOM
          </span>
          <ToolbarButton
            onClick={() => stepRange(-1)}
            title="Zoom in (smaller range)"
          >
            −
          </ToolbarButton>
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              color: "#cbe8d0",
              minWidth: 36,
              textAlign: "center",
            }}
          >
            {display.rangeNm} nm
          </span>
          <ToolbarButton
            onClick={() => stepRange(1)}
            title="Zoom out (larger range)"
          >
            +
          </ToolbarButton>
        </>
      )}
    </div>
  );
}

function ToolbarButton(props: {
  active?: boolean;
  onClick: () => void;
  title?: string;
  children: React.ReactNode;
}): JSX.Element {
  const active = props.active === true;
  return (
    <button
      onClick={props.onClick}
      title={props.title}
      aria-pressed={props.active === undefined ? undefined : active}
      style={{
        background: active ? "var(--accent)" : "transparent",
        color: active ? "var(--accent-fg)" : "#cbe8d0",
        border: `1px solid ${active ? "var(--accent)" : "rgba(255,255,255,0.2)"}`,
        borderRadius: 4,
        padding: "3px 8px",
        fontSize: 11,
        fontWeight: 600,
        cursor: "pointer",
        fontFamily: "var(--font-mono)",
        letterSpacing: 0.5,
      }}
    >
      {props.children}
    </button>
  );
}

function ChartMarkPopover(props: {
  mark: Mark;
  x: number;
  y: number;
  containerSize: { w: number; h: number };
  onClose: () => void;
}): JSX.Element {
  const { mark, x, y, containerSize } = props;
  const POPOVER_W = 220;
  const flip = x + 16 + POPOVER_W > containerSize.w;
  const left = flip
    ? Math.max(8, x - 16 - POPOVER_W)
    : Math.min(containerSize.w - POPOVER_W - 8, x + 16);
  const top = Math.max(8, Math.min(containerSize.h - 130, y - 60));
  return (
    <div
      role="dialog"
      onClick={(e) => e.stopPropagation()}
      style={{
        position: "absolute",
        left,
        top,
        width: POPOVER_W,
        background: "rgba(11, 18, 32, 0.94)",
        border: "1px solid rgba(255, 220, 130, 0.6)",
        borderRadius: 6,
        padding: "10px 12px",
        color: "#f3f4f6",
        fontSize: 12,
        fontFamily: "ui-sans-serif, system-ui",
        boxShadow: "0 6px 16px rgba(0,0,0,0.5)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
        <strong style={{ fontSize: 13, color: "#fbbf24" }}>{mark.name}</strong>
        <button
          onClick={props.onClose}
          aria-label="Close"
          style={{
            background: "transparent",
            border: "none",
            color: "#9aa6b8",
            fontSize: 14,
            lineHeight: 1,
            cursor: "pointer",
            padding: 0,
          }}
        >
          ×
        </button>
      </div>
      <div style={{ marginTop: 4, fontSize: 11, color: "#9aa6b8" }}>
        {mark.type}
        {mark.category !== undefined ? ` · ${mark.category.replace(/-/g, " ")}` : ""}
      </div>
      <div
        style={{
          marginTop: 4,
          fontFamily: "ui-monospace, monospace",
          fontSize: 11,
          color: "#9aa6b8",
        }}
      >
        {mark.position.lat.toFixed(4)}°N, {Math.abs(mark.position.lon).toFixed(4)}°W
      </div>
      {mark.light !== undefined && (
        <div
          style={{
            marginTop: 8,
            paddingTop: 8,
            borderTop: "1px solid rgba(255,220,130,0.25)",
            fontSize: 11,
            lineHeight: 1.4,
          }}
        >
          <span style={{ color: "#fbbf24", fontWeight: 600 }}>LIGHT</span>
          <div style={{ fontFamily: "ui-monospace, monospace", marginTop: 2 }}>
            {mark.light.character}
          </div>
          <div style={{ marginTop: 2, color: "#9aa6b8" }}>
            {describeLight(mark.light)}
          </div>
          {mark.light.fogSignal !== undefined && (
            <div style={{ marginTop: 6 }}>
              <span style={{ color: "#fbbf24", fontWeight: 600 }}>FOG</span>{" "}
              <span style={{ fontFamily: "ui-monospace, monospace" }}>
                {mark.light.fogSignal}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ChartTargetPopover(props: {
  target: TargetState;
  x: number;
  y: number;
  containerSize: { w: number; h: number };
  onClose: () => void;
}): JSX.Element {
  const { target: t, x, y, containerSize } = props;
  const POPOVER_W = 240;
  const flip = x + 16 + POPOVER_W > containerSize.w;
  const left = flip
    ? Math.max(8, x - 16 - POPOVER_W)
    : Math.min(containerSize.w - POPOVER_W - 8, x + 16);
  const top = Math.max(8, Math.min(containerSize.h - 220, y - 60));
  const shape = dayShapeFor(t.vesselType, t.state);
  const lights = lightsFor(t.vesselType, t.state);
  const fog = fogSignalFor(t.vesselType, t.state);
  return (
    <div
      role="dialog"
      onClick={(e) => e.stopPropagation()}
      style={{
        position: "absolute",
        left,
        top,
        width: POPOVER_W,
        background: "rgba(11, 18, 32, 0.94)",
        border: "1px solid rgba(56, 189, 248, 0.55)",
        borderRadius: 6,
        padding: "10px 12px",
        color: "#f3f4f6",
        fontSize: 12,
        fontFamily: "ui-sans-serif, system-ui",
        boxShadow: "0 6px 16px rgba(0,0,0,0.5)",
        maxHeight: containerSize.h - 16,
        overflowY: "auto",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
        <strong style={{ fontSize: 13, color: "var(--accent)" }}>
          {t.ais?.name ?? t.id}
        </strong>
        <button
          onClick={props.onClose}
          aria-label="Close"
          style={{
            background: "transparent",
            border: "none",
            color: "#9aa6b8",
            fontSize: 14,
            lineHeight: 1,
            cursor: "pointer",
            padding: 0,
          }}
        >
          ×
        </button>
      </div>
      <div style={{ marginTop: 4, fontSize: 11, color: "#9aa6b8" }}>
        {t.vesselType}
        {t.state !== undefined && t.state !== "underway"
          ? ` · ${t.state.replace(/-/g, " ")}`
          : ""}
      </div>
      <div
        style={{
          marginTop: 8,
          paddingTop: 8,
          borderTop: "1px solid rgba(56,189,248,0.2)",
          display: "flex",
          gap: 10,
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "center" }}>
          {shape !== undefined ? <DayShapeIcon shape={shape} size={32} /> : null}
          {lights !== undefined ? (
            <LightArrangementDiagram arrangement={lights} width={72} />
          ) : null}
        </div>
        <div style={{ flex: 1, fontSize: 11, lineHeight: 1.4 }}>
          {shape !== undefined && (
            <div style={{ marginBottom: 6 }}>
              <span style={{ color: "var(--accent)", fontWeight: 600 }}>DAY</span>
              <div style={{ color: "#9aa6b8" }}>{describeDayShape(shape)}</div>
            </div>
          )}
          {lights !== undefined && (
            <div style={{ marginBottom: 6 }}>
              <span style={{ color: "var(--accent)", fontWeight: 600 }}>LIGHTS</span>
              <div style={{ color: "#9aa6b8" }}>{lights.description}</div>
            </div>
          )}
          {fog !== undefined && (
            <div>
              <span style={{ color: "var(--accent)", fontWeight: 600 }}>FOG</span>{" "}
              <span style={{ fontFamily: "ui-monospace, monospace" }}>{fog.pattern}</span>
              <div style={{ color: "#9aa6b8" }}>{fog.description}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const overlayStyle: React.CSSProperties = {
  position: "absolute",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 13,
  color: "#444",
  background: "rgba(255,255,255,0.6)",
};

interface Projection {
  toXY: (p: LatLon) => { x: number; y: number };
  fromXY: (xy: { x: number; y: number }) => LatLon;
  /** Visible half-extent in nautical miles (matches radar range scale). */
  rangeNm: number;
  /** Centre point (own ship) in lat/lon, for clip / outside checks. */
  centre: LatLon;
  /** Up-bearing in degrees true. 0 = north-up, ownCog = head-up. */
  upDeg: number;
}

/**
 * Build a chart projection centred on own ship, scaled to match the
 * radar's range. Supports north-up (default chart-table convention)
 * and head-up (rotates the world so own ship's COG points up — handy
 * for cross-referencing with a head-up radar).
 *
 * Latitude/longitude get the standard small-area Mercator treatment:
 * lat scales 1:1, lon scales by cos(centreLat) so the aspect doesn't
 * squash near the poles.
 */
function makeProjection(
  centre: LatLon,
  rangeNm: number,
  size: { w: number; h: number },
  upDeg: number,
): Projection {
  const lonScale = Math.cos((centre.lat * Math.PI) / 180);
  const halfExtentPx = Math.min(size.w, size.h) / 2;
  const pxPerNm = halfExtentPx / rangeNm;
  const cx = size.w / 2;
  const cy = size.h / 2;
  // Rotation applied to the unrotated north-up canvas coords. -upDeg
  // converts so the bearing `upDeg` points to up (-y) on the screen.
  const rotRad = (-upDeg * Math.PI) / 180;
  const cosR = Math.cos(rotRad);
  const sinR = Math.sin(rotRad);
  return {
    centre,
    rangeNm,
    upDeg,
    toXY: (p: LatLon) => {
      const dxNm = (p.lon - centre.lon) * lonScale * 60;
      const dyNm = (p.lat - centre.lat) * 60;
      // Pre-rotation canvas coords (north-up).
      const cx0 = dxNm * pxPerNm;
      const cy0 = -dyNm * pxPerNm;
      // Rotate around the centre by -upDeg.
      return {
        x: cx + cx0 * cosR - cy0 * sinR,
        y: cy + cx0 * sinR + cy0 * cosR,
      };
    },
    fromXY: (xy) => {
      const rx = xy.x - cx;
      const ry = xy.y - cy;
      // Inverse rotation = rotate by +upDeg.
      const cx0 = rx * cosR + ry * sinR;
      const cy0 = -rx * sinR + ry * cosR;
      const dxNm = cx0 / pxPerNm;
      const dyNm = -cy0 / pxPerNm;
      return {
        lat: centre.lat + dyNm / 60,
        lon: centre.lon + dxNm / (60 * lonScale),
      };
    },
  };
}

function drawChart(
  ctx: CanvasRenderingContext2D,
  size: { w: number; h: number },
  area: AreaData | undefined,
  snapshot: WorldSnapshot | null,
  display: DisplayState,
): void {
  ctx.clearRect(0, 0, size.w, size.h);
  ctx.fillStyle = COLOURS.sea;
  ctx.fillRect(0, 0, size.w, size.h);
  if (area === undefined) return;

  // Centre on own ship if we have a snapshot, otherwise on the area's
  // bounding-box midpoint (initial paint while waiting for tick 0).
  const centre: LatLon =
    snapshot !== null
      ? snapshot.ownShip.position
      : {
          lat: (area.boundingBox.nw.lat + area.boundingBox.se.lat) / 2,
          lon: (area.boundingBox.nw.lon + area.boundingBox.se.lon) / 2,
        };
  const upDeg =
    display.chartOrientation === "head-up" && snapshot !== null
      ? snapshot.ownShip.cog
      : 0;
  const proj = makeProjection(centre, display.rangeNm, size, upDeg);

  drawGrid(ctx, size, proj);
  drawLanes(ctx, area, proj);
  drawDepthContours(ctx, area, proj);
  drawCoastline(ctx, area, size, proj);
  drawTidalAnomalies(ctx, area, proj);
  if (display.showMarks) {
    drawMarks(ctx, area, proj, display.nightMode, snapshot?.simTimeSeconds ?? 0);
  }
  if (snapshot !== null) {
    drawTargets(ctx, snapshot, proj, display);
    drawSarts(ctx, snapshot, proj);
    drawOwnShip(ctx, snapshot, proj);
    drawRangeRing(ctx, size, snapshot.ownShip.position, display.rangeNm, proj);
  }
  drawScaleAndLabel(ctx, size, area, display.rangeNm, display.chartOrientation);
}

function drawGrid(
  ctx: CanvasRenderingContext2D,
  size: { w: number; h: number },
  proj: Projection,
): void {
  ctx.strokeStyle = COLOURS.grid;
  ctx.lineWidth = 0.5;
  // Pick a grid step in degrees that gives roughly 4–8 lines across
  // the visible area. Visible lat span = 2 * rangeNm / 60.
  const visibleLatDeg = (proj.rangeNm * 2) / 60;
  const candidates = [0.005, 0.01, 0.02, 0.05, 0.1, 0.25, 0.5];
  const step = candidates.find((c) => visibleLatDeg / c <= 10) ?? 1;
  // Latitude lines (horizontal on a north-up chart).
  const latStart = Math.floor((proj.centre.lat - visibleLatDeg / 2) / step) * step;
  const latEnd = proj.centre.lat + visibleLatDeg / 2;
  for (let lat = latStart; lat <= latEnd + 1e-9; lat += step) {
    const y = proj.toXY({ lat, lon: proj.centre.lon }).y;
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(size.w, y);
    ctx.stroke();
  }
  // Longitude lines (vertical).
  const lonScale = Math.cos((proj.centre.lat * Math.PI) / 180);
  const visibleLonDeg = (proj.rangeNm * 2) / (60 * lonScale);
  const lonStart = Math.floor((proj.centre.lon - visibleLonDeg / 2) / step) * step;
  const lonEnd = proj.centre.lon + visibleLonDeg / 2;
  for (let lon = lonStart; lon <= lonEnd + 1e-9; lon += step) {
    const x = proj.toXY({ lat: proj.centre.lat, lon }).x;
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, size.h);
    ctx.stroke();
  }
}

function drawCoastline(
  ctx: CanvasRenderingContext2D,
  area: AreaData,
  size: { w: number; h: number },
  proj: Projection,
): void {
  // Land fill: closed rings (islands and full landmasses) fill as-is.
  // Open rings (mainland clipped at the area's bbox) close out to the
  // canvas edge on the land side per area-data's mainlandLandSide
  // hint, so the fill represents land beyond the chart's view rather
  // than running to the wrong edge.
  ctx.save();
  ctx.fillStyle = COLOURS.land;
  ctx.strokeStyle = COLOURS.landEdge;
  ctx.lineWidth = 1.2;
  const landSide = mainlandLandSide(area.area);
  for (const ring of area.coastline) {
    if (ring.length < 3) continue;
    ctx.beginPath();
    const first = proj.toXY(ring[0]!);
    ctx.moveTo(first.x, first.y);
    for (let j = 1; j < ring.length; j++) {
      const xy = proj.toXY(ring[j]!);
      ctx.lineTo(xy.x, xy.y);
    }
    if (!isClosedRing(ring) && landSide !== "skip") {
      const lastXY = proj.toXY(ring[ring.length - 1]!);
      const firstXY = proj.toXY(ring[0]!);
      const closeY = landSide === "south" ? size.h * 2 : -size.h;
      ctx.lineTo(lastXY.x, closeY);
      ctx.lineTo(firstXY.x, closeY);
    }
    ctx.closePath();
    ctx.fill();
  }
  // Re-stroke just the coastline polylines (not the canvas-edge
  // closing extensions).
  for (const ring of area.coastline) {
    if (ring.length < 2) continue;
    ctx.beginPath();
    const first = proj.toXY(ring[0]!);
    ctx.moveTo(first.x, first.y);
    for (let j = 1; j < ring.length; j++) {
      const xy = proj.toXY(ring[j]!);
      ctx.lineTo(xy.x, xy.y);
    }
    ctx.stroke();
  }
  ctx.restore();
}

function drawDepthContours(
  ctx: CanvasRenderingContext2D,
  area: AreaData,
  proj: Projection,
): void {
  ctx.save();
  ctx.strokeStyle = COLOURS.contour;
  ctx.lineWidth = 0.6;
  ctx.setLineDash([3, 3]);
  for (const c of area.depthContours) {
    if (c.line.length < 2) continue;
    ctx.beginPath();
    const first = proj.toXY(c.line[0]!);
    ctx.moveTo(first.x, first.y);
    for (let j = 1; j < c.line.length; j++) {
      const xy = proj.toXY(c.line[j]!);
      ctx.lineTo(xy.x, xy.y);
    }
    ctx.stroke();
  }
  ctx.restore();
}

function drawLanes(
  ctx: CanvasRenderingContext2D,
  area: AreaData,
  proj: Projection,
): void {
  ctx.save();
  ctx.strokeStyle = COLOURS.laneFerry;
  ctx.lineWidth = 1.2;
  ctx.setLineDash([6, 4]);
  for (const points of area.lanes.values()) {
    if (points.length < 2) continue;
    ctx.beginPath();
    const first = proj.toXY(points[0]!);
    ctx.moveTo(first.x, first.y);
    for (let j = 1; j < points.length; j++) {
      const xy = proj.toXY(points[j]!);
      ctx.lineTo(xy.x, xy.y);
    }
    ctx.stroke();
  }
  ctx.restore();
}

function drawMarks(
  ctx: CanvasRenderingContext2D,
  area: AreaData,
  proj: Projection,
  nightMode: boolean,
  simSeconds: number,
): void {
  ctx.save();
  for (const mark of area.marks) {
    const xy = proj.toXY(mark.position);
    if (mark.racon !== undefined) {
      ctx.fillStyle = COLOURS.raconHalo;
      ctx.beginPath();
      ctx.arc(xy.x, xy.y, 5, 0, Math.PI * 2);
      ctx.fill();
    }
    if (mark.type === "tower" || mark.type === "light") {
      ctx.fillStyle = COLOURS.tower;
      ctx.fillRect(xy.x - 2.2, xy.y - 2.2, 4.4, 4.4);
    } else if (mark.category !== undefined) {
      // When night-mode is on AND this mark has a defined light, the
      // light character drives a brightness boost on the IALA symbol
      // itself — the buoy's coloured face appears to "wink" with the
      // declared rhythm rather than just a halo above it.
      const litNow =
        nightMode && mark.light !== undefined
          ? lightFrame(mark.light, simSeconds).on
          : false;
      paintIalaBuoy(ctx, xy.x, xy.y, mark.category, litNow);
    } else {
      ctx.fillStyle = COLOURS.buoy;
      ctx.beginPath();
      ctx.arc(xy.x, xy.y, 2.2, 0, Math.PI * 2);
      ctx.fill();
    }
    // Night-mode pulse: paint the light's current colour as a glowing
    // halo over the chart symbol while the light is "on" by its
    // declared character. Same source-of-truth (lightFrame) as the
    // PPI so the two views flash in lockstep.
    if (nightMode && mark.light !== undefined) {
      const frame = lightFrame(mark.light, simSeconds);
      if (frame.on) {
        ctx.save();
        ctx.shadowColor = frame.color;
        ctx.shadowBlur = 10;
        ctx.fillStyle = frame.color;
        ctx.globalAlpha = frame.intensity;
        ctx.beginPath();
        ctx.arc(xy.x, xy.y, 4, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }
    }
  }
  ctx.restore();
}

const RED = "#c0212f";
const GREEN = "#1e9e4f";
const YELLOW = "#e8b80f";
const BLACK = "#0a0a10";
const WHITE = "#f5f5f5";

/**
 * Paint an IALA-Region-A buoy symbol with the right colour scheme
 * and topmark. Sized for a chart-table view at typical zoom — small
 * enough not to clutter, distinct enough that a student can read off
 * the buoy category at a glance.
 */
function paintIalaBuoy(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  category: import("@radar-sim/area-data").BuoyCategory,
  lit: boolean = false,
): void {
  // When the light is on right now, paint a faint glow under the
  // IALA symbol so the symbol itself reads as winking. Subtle —
  // the existing halo above the buoy continues to do the visible
  // pulse work.
  if (lit) {
    ctx.save();
    ctx.shadowColor = "rgba(254, 243, 199, 0.95)";
    ctx.shadowBlur = 8;
    ctx.fillStyle = "rgba(254, 243, 199, 0.4)";
    ctx.beginPath();
    ctx.arc(x, y, 5, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
  const r = 3;
  switch (category) {
    case "port-hand":
      // Red can — flat-topped cylinder shape rendered as a small red
      // square anchored at the position.
      ctx.fillStyle = RED;
      ctx.fillRect(x - r, y - r, r * 2, r * 2);
      return;
    case "starboard-hand":
      // Green cone, point up.
      ctx.fillStyle = GREEN;
      ctx.beginPath();
      ctx.moveTo(x, y - r);
      ctx.lineTo(x + r, y + r);
      ctx.lineTo(x - r, y + r);
      ctx.closePath();
      ctx.fill();
      return;
    case "north-cardinal":
      // Black-over-yellow pillar, top mark = 2 cones point-up.
      paintCardinalPillar(ctx, x, y, [BLACK, YELLOW]);
      paintCardinalTopmark(ctx, x, y - r - 1, "up-up");
      return;
    case "south-cardinal":
      paintCardinalPillar(ctx, x, y, [YELLOW, BLACK]);
      paintCardinalTopmark(ctx, x, y - r - 1, "down-down");
      return;
    case "east-cardinal":
      paintCardinalPillar(ctx, x, y, [BLACK, YELLOW, BLACK]);
      paintCardinalTopmark(ctx, x, y - r - 1, "base-to-base");
      return;
    case "west-cardinal":
      paintCardinalPillar(ctx, x, y, [YELLOW, BLACK, YELLOW]);
      paintCardinalTopmark(ctx, x, y - r - 1, "apex-to-apex");
      return;
    case "safe-water":
      // Red and white vertical stripes, red sphere top mark.
      paintStripedBuoy(ctx, x, y, [RED, WHITE], "vertical");
      ctx.fillStyle = RED;
      ctx.beginPath();
      ctx.arc(x, y - r - 2, 1.4, 0, Math.PI * 2);
      ctx.fill();
      return;
    case "isolated-danger":
      // Black with red horizontal band, two-balls top mark.
      paintStripedBuoy(ctx, x, y, [BLACK, RED, BLACK], "horizontal");
      ctx.fillStyle = BLACK;
      ctx.beginPath();
      ctx.arc(x - 1.4, y - r - 2, 1.1, 0, Math.PI * 2);
      ctx.arc(x + 1.4, y - r - 2, 1.1, 0, Math.PI * 2);
      ctx.fill();
      return;
    case "special":
      // Yellow X top mark.
      ctx.fillStyle = YELLOW;
      ctx.beginPath();
      ctx.arc(x, y, r - 0.3, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = YELLOW;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x - 2, y - r - 3);
      ctx.lineTo(x + 2, y - r - 1);
      ctx.moveTo(x + 2, y - r - 3);
      ctx.lineTo(x - 2, y - r - 1);
      ctx.stroke();
      return;
    case "preferred-port":
      // Red can with green band.
      ctx.fillStyle = RED;
      ctx.fillRect(x - r, y - r, r * 2, r * 2);
      ctx.fillStyle = GREEN;
      ctx.fillRect(x - r, y - 0.6, r * 2, 1.2);
      return;
    case "preferred-starboard":
      // Green cone with red band.
      ctx.fillStyle = GREEN;
      ctx.beginPath();
      ctx.moveTo(x, y - r);
      ctx.lineTo(x + r, y + r);
      ctx.lineTo(x - r, y + r);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = RED;
      ctx.fillRect(x - r, y - 0.4, r * 2, 0.8);
      return;
  }
}

function paintCardinalPillar(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  bands: ReadonlyArray<string>,
): void {
  const r = 3;
  const bandH = (r * 2) / bands.length;
  for (let i = 0; i < bands.length; i++) {
    ctx.fillStyle = bands[i]!;
    ctx.fillRect(x - r * 0.6, y - r + i * bandH, r * 1.2, bandH);
  }
}

function paintCardinalTopmark(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  shape: "up-up" | "down-down" | "base-to-base" | "apex-to-apex",
): void {
  ctx.fillStyle = BLACK;
  const s = 1.4;
  switch (shape) {
    case "up-up":
      // Both cones point up.
      ctx.beginPath();
      ctx.moveTo(x, y - s * 2.4);
      ctx.lineTo(x + s, y - s * 0.4);
      ctx.lineTo(x - s, y - s * 0.4);
      ctx.closePath();
      ctx.fill();
      ctx.beginPath();
      ctx.moveTo(x, y - s * 0.2);
      ctx.lineTo(x + s, y + s * 1.8);
      ctx.lineTo(x - s, y + s * 1.8);
      ctx.closePath();
      ctx.fill();
      return;
    case "down-down":
      ctx.beginPath();
      ctx.moveTo(x - s, y - s * 2.4);
      ctx.lineTo(x + s, y - s * 2.4);
      ctx.lineTo(x, y - s * 0.4);
      ctx.closePath();
      ctx.fill();
      ctx.beginPath();
      ctx.moveTo(x - s, y - s * 0.2);
      ctx.lineTo(x + s, y - s * 0.2);
      ctx.lineTo(x, y + s * 1.8);
      ctx.closePath();
      ctx.fill();
      return;
    case "base-to-base":
      // Bases meet in the middle: lower cone point-down, upper cone
      // point-up.
      ctx.beginPath();
      ctx.moveTo(x, y - s * 2.4);
      ctx.lineTo(x + s, y - s * 0.4);
      ctx.lineTo(x - s, y - s * 0.4);
      ctx.closePath();
      ctx.fill();
      ctx.beginPath();
      ctx.moveTo(x - s, y - s * 0.4);
      ctx.lineTo(x + s, y - s * 0.4);
      ctx.lineTo(x, y + s * 1.6);
      ctx.closePath();
      ctx.fill();
      return;
    case "apex-to-apex":
      ctx.beginPath();
      ctx.moveTo(x - s, y - s * 2.4);
      ctx.lineTo(x + s, y - s * 2.4);
      ctx.lineTo(x, y - s * 0.4);
      ctx.closePath();
      ctx.fill();
      ctx.beginPath();
      ctx.moveTo(x, y - s * 0.4);
      ctx.lineTo(x + s, y + s * 1.6);
      ctx.lineTo(x - s, y + s * 1.6);
      ctx.closePath();
      ctx.fill();
      return;
  }
}

function paintStripedBuoy(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  stripes: ReadonlyArray<string>,
  axis: "vertical" | "horizontal",
): void {
  const r = 3;
  const w = (r * 1.4) / stripes.length;
  for (let i = 0; i < stripes.length; i++) {
    ctx.fillStyle = stripes[i]!;
    if (axis === "vertical") {
      ctx.fillRect(x - r * 0.7 + i * w, y - r * 0.7, w, r * 1.4);
    } else {
      const h = (r * 1.4) / stripes.length;
      ctx.fillRect(x - r * 0.7, y - r * 0.7 + i * h, r * 1.4, h);
    }
  }
}

function drawTargets(
  ctx: CanvasRenderingContext2D,
  snapshot: WorldSnapshot,
  proj: Projection,
  display: DisplayState,
): void {
  // Draw inter-vessel relationships first so they sit underneath the
  // hull silhouettes: pair-trawler nets, alongside-tow attachments.
  drawTowAlongsidePairs(ctx, snapshot, proj);
  drawPairTrawlerNets(ctx, snapshot, proj);

  ctx.save();
  ctx.fillStyle = COLOURS.target;
  ctx.strokeStyle = COLOURS.target;
  ctx.lineWidth = 0.8;
  for (const t of snapshot.targets) {
    if (!t.visible) continue;
    const xy = proj.toXY(t.position);
    if (display.chartVesselPaint === "ais") {
      // AIS-style: triangle + COG vector + (in head-up the symbol
      // turns with the screen, so subtract the upDeg from cog).
      const screenCog = ((t.cog - proj.upDeg) % 360 + 360) % 360;
      paintArrow(ctx, xy.x, xy.y, screenCog, 5);
      const len = Math.min(28, t.sog * 1.4);
      const rad = ((screenCog - 90) * Math.PI) / 180;
      ctx.beginPath();
      ctx.moveTo(xy.x, xy.y);
      ctx.lineTo(xy.x + Math.cos(rad) * len, xy.y + Math.sin(rad) * len);
      ctx.stroke();
    } else {
      // Silhouette mode: filled hull outline pointing along COG, with
      // navigation lights painted at correct positions when night
      // mode is on. No speed vector — this is the "what you'd see by
      // eye" view.
      const screenCog = ((t.cog - proj.upDeg) % 360 + 360) % 360;
      paintHullSilhouette(ctx, xy.x, xy.y, screenCog, t, display);
    }
  }
  ctx.restore();
}

/**
 * Tidal anomaly overlays — disturbed-water hazard zones at headlands
 * (St Catherine's race, Hurst overfalls, Bramble flood eddy,
 * Portland race in WP). Painted as severity-graded translucent
 * patches with diagonal hatch lines so they read as "rough water"
 * rather than land or shoal.
 */
function drawTidalAnomalies(
  ctx: CanvasRenderingContext2D,
  area: AreaData,
  proj: Projection,
): void {
  const anomalies = getTidalAnomalies(area.area);
  if (anomalies.length === 0) return;
  ctx.save();
  for (const a of anomalies) {
    if (a.polygon.length < 3) continue;
    // Project polygon to canvas coords.
    const pts = a.polygon.map((p) => proj.toXY(p));
    // Filled translucent patch.
    const opacity = 0.18 + a.severity * 0.18;
    ctx.fillStyle = `rgba(120, 60, 30, ${opacity})`;
    ctx.beginPath();
    ctx.moveTo(pts[0]!.x, pts[0]!.y);
    for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i]!.x, pts[i]!.y);
    ctx.closePath();
    ctx.fill();
    // Hatch lines so the area reads as rough/disturbed water rather
    // than just a smooth tint. Clip to the polygon, draw parallel
    // diagonals, restore.
    ctx.save();
    ctx.clip();
    ctx.strokeStyle = `rgba(120, 60, 30, ${0.4 + a.severity * 0.3})`;
    ctx.lineWidth = 0.6;
    const minX = Math.min(...pts.map((p) => p.x));
    const maxX = Math.max(...pts.map((p) => p.x));
    const minY = Math.min(...pts.map((p) => p.y));
    const maxY = Math.max(...pts.map((p) => p.y));
    const step = 4;
    for (let off = minX - (maxY - minY); off < maxX + (maxY - minY); off += step) {
      ctx.beginPath();
      ctx.moveTo(off, minY);
      ctx.lineTo(off + (maxY - minY), maxY);
      ctx.stroke();
    }
    ctx.restore();
    // Outline.
    ctx.strokeStyle = `rgba(120, 60, 30, ${0.55 + a.severity * 0.3})`;
    ctx.lineWidth = 0.9;
    ctx.beginPath();
    ctx.moveTo(pts[0]!.x, pts[0]!.y);
    for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i]!.x, pts[i]!.y);
    ctx.closePath();
    ctx.stroke();
    // Centroid label so the student knows what they're looking at.
    const cx = pts.reduce((s, p) => s + p.x, 0) / pts.length;
    const cy = pts.reduce((s, p) => s + p.y, 0) / pts.length;
    ctx.fillStyle = "rgba(60, 25, 12, 0.85)";
    ctx.font = "10px ui-sans-serif, system-ui";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(a.name, cx, cy);
  }
  ctx.restore();
}

/**
 * For each pair of vessels both flagged towing-alongside that sit
 * within ~50m of each other on roughly the same heading, draw a
 * solid attachment line between them — visually conveying that they
 * are made up alongside (the tug pushing/towing the barge).
 */
function drawTowAlongsidePairs(
  ctx: CanvasRenderingContext2D,
  snapshot: WorldSnapshot,
  proj: Projection,
): void {
  const ts = snapshot.targets.filter(
    (t) => t.visible && t.state === "towing-alongside",
  );
  if (ts.length < 2) return;
  ctx.save();
  ctx.strokeStyle = "rgba(20, 30, 40, 0.6)";
  ctx.lineWidth = 3;
  ctx.lineCap = "round";
  for (let i = 0; i < ts.length; i++) {
    for (let j = i + 1; j < ts.length; j++) {
      const a = ts[i]!;
      const b = ts[j]!;
      const dlat = (b.position.lat - a.position.lat) * 60;
      const dlon =
        (b.position.lon - a.position.lon) *
        60 *
        Math.cos((a.position.lat * Math.PI) / 180);
      const distNm = Math.sqrt(dlat * dlat + dlon * dlon);
      const headingDelta = Math.abs(((a.cog - b.cog) % 360 + 540) % 360 - 180);
      // 0.05 nm ≈ 90 m — an alongside-tow combo is normally hull-to-hull.
      if (distNm <= 0.05 && headingDelta <= 30) {
        const ax = proj.toXY(a.position);
        const bx = proj.toXY(b.position);
        ctx.beginPath();
        ctx.moveTo(ax.x, ax.y);
        ctx.lineTo(bx.x, bx.y);
        ctx.stroke();
      }
    }
  }
  ctx.restore();
}

/**
 * Pair-trawling: when two fishing vessels are within ~0.4 nm of each
 * other on similar headings, paint a faint net line between them.
 * Real pair-trawlers tow a single bottom net spread between the pair
 * — this rendering is the canonical "two boats with a net" cue.
 */
function drawPairTrawlerNets(
  ctx: CanvasRenderingContext2D,
  snapshot: WorldSnapshot,
  proj: Projection,
): void {
  const trawlers = snapshot.targets.filter(
    (t) => t.visible && (t.vesselType === "fishing" || t.state === "fishing"),
  );
  if (trawlers.length < 2) return;
  ctx.save();
  ctx.strokeStyle = "rgba(120, 90, 30, 0.7)";
  ctx.lineWidth = 1.2;
  ctx.setLineDash([4, 3]);
  for (let i = 0; i < trawlers.length; i++) {
    for (let j = i + 1; j < trawlers.length; j++) {
      const a = trawlers[i]!;
      const b = trawlers[j]!;
      const dlat = (b.position.lat - a.position.lat) * 60;
      const dlon =
        (b.position.lon - a.position.lon) *
        60 *
        Math.cos((a.position.lat * Math.PI) / 180);
      const distNm = Math.sqrt(dlat * dlat + dlon * dlon);
      const headingDelta = Math.abs(((a.cog - b.cog) % 360 + 540) % 360 - 180);
      if (distNm <= 0.4 && headingDelta <= 25 && a.sog > 1 && b.sog > 1) {
        const ax = proj.toXY(a.position);
        const bx = proj.toXY(b.position);
        // Offset the net line slightly astern so it visually trails
        // both boats rather than overlaying them.
        const midX = (ax.x + bx.x) / 2;
        const midY = (ax.y + bx.y) / 2;
        ctx.beginPath();
        ctx.moveTo(ax.x, ax.y);
        ctx.quadraticCurveTo(midX, midY + 4, bx.x, bx.y);
        ctx.stroke();
      }
    }
  }
  ctx.restore();
}

/**
 * Per-vessel-type hull silhouette outline (in vessel-local coords:
 * +x = forward toward bow, +y = starboard, units = relative-to-len).
 * Each vessel type gets a distinct profile so a navigator can tell a
 * container from a fastcat from a fishing trawler at a glance.
 */
function hullProfile(target: TargetState): {
  pts: ReadonlyArray<[number, number]>;
  fill: string;
  beamFactor: number;
} {
  switch (target.vesselType) {
    case "container":
      // Long boxy hull, blunt bow.
      return {
        pts: [
          [0.55, 0], [0.45, 0.18], [-0.45, 0.18],
          [-0.45, -0.18], [0.45, -0.18],
        ],
        fill: "#1d2530",
        beamFactor: 0.36,
      };
    case "ferry":
      // Boxier profile — passenger superstructure makes the
      // silhouette look full-bodied.
      return {
        pts: [
          [0.5, 0], [0.4, 0.2], [-0.5, 0.2],
          [-0.5, -0.2], [0.4, -0.2],
        ],
        fill: "#23303a",
        beamFactor: 0.4,
      };
    case "fastcat":
      // Catamaran — twin slim hulls. Approximate as a single
      // narrow rectangle with two parallel lines through it.
      return {
        pts: [
          [0.6, 0], [0.5, 0.16], [-0.4, 0.16],
          [-0.4, -0.16], [0.5, -0.16],
        ],
        fill: "#3a4d5e",
        beamFactor: 0.32,
      };
    case "hovercraft":
      // Square skirt — almost as wide as it is long.
      return {
        pts: [
          [0.45, 0], [0.4, 0.3], [-0.4, 0.3],
          [-0.4, -0.3], [0.4, -0.3],
        ],
        fill: "#2a3f4a",
        beamFactor: 0.6,
      };
    case "tug":
      // Stout pushy profile.
      return {
        pts: [
          [0.5, 0], [0.35, 0.25], [-0.45, 0.25],
          [-0.45, -0.25], [0.35, -0.25],
        ],
        fill: "#3a2d20",
        beamFactor: 0.5,
      };
    case "fishing":
      // Trawler stern, sharper bow.
      return {
        pts: [
          [0.55, 0], [0.4, 0.22], [-0.5, 0.18],
          [-0.5, -0.18], [0.4, -0.22],
        ],
        fill: "#3a2f1a",
        beamFactor: 0.42,
      };
    case "yacht-sail":
      // Slender sailboat profile.
      return {
        pts: [
          [0.6, 0], [0.3, 0.1], [-0.45, 0.1],
          [-0.45, -0.1], [0.3, -0.1],
        ],
        fill: "#3a4a2a",
        beamFactor: 0.22,
      };
    case "yacht-power":
      // Beamier than sail.
      return {
        pts: [
          [0.55, 0], [0.35, 0.16], [-0.4, 0.16],
          [-0.4, -0.16], [0.35, -0.16],
        ],
        fill: "#2d3a4a",
        beamFactor: 0.32,
      };
    case "rib":
      // RIB: pointed bow, fat tubes — show as long lozenge.
      return {
        pts: [
          [0.6, 0], [0.4, 0.18], [-0.4, 0.16],
          [-0.4, -0.16], [0.4, -0.18],
        ],
        fill: "#3a3030",
        beamFactor: 0.28,
      };
    case "naval":
      return {
        pts: [
          [0.6, 0], [0.4, 0.14], [-0.5, 0.14],
          [-0.5, -0.14], [0.4, -0.14],
        ],
        fill: "#1a1a22",
        beamFactor: 0.28,
      };
    case "pilot":
      // Pilot launch — chunky and short.
      return {
        pts: [
          [0.5, 0], [0.35, 0.22], [-0.4, 0.22],
          [-0.4, -0.22], [0.35, -0.22],
        ],
        fill: "#1a3a4a",
        beamFactor: 0.44,
      };
    case "dredger":
      // Wide, blocky — boom forward of beam.
      return {
        pts: [
          [0.4, 0], [0.4, 0.3], [-0.5, 0.3],
          [-0.5, -0.3], [0.4, -0.3],
        ],
        fill: "#3a2a1a",
        beamFactor: 0.6,
      };
    case "minesweeper":
      // Hunt-class — relatively chunky.
      return {
        pts: [
          [0.55, 0], [0.4, 0.2], [-0.45, 0.2],
          [-0.45, -0.2], [0.4, -0.2],
        ],
        fill: "#2a2a30",
        beamFactor: 0.38,
      };
    case "buoy":
      return {
        pts: [
          [0.4, 0], [0.4, 0.4], [-0.4, 0.4],
          [-0.4, -0.4], [0.4, -0.4],
        ],
        fill: "#1f5b8d",
        beamFactor: 0.8,
      };
  }
}

/**
 * Filled vessel outline pointing along COG, sized roughly by RCS so
 * a container looks bigger than a yacht. The silhouette shape comes
 * from the vessel's type (container, fastcat, fishing, etc.). When
 * night mode is on, paint the navigation-light arrangement on top
 * of the hull at the correct positions — so you can see at a glance
 * which side faces you.
 */
function paintHullSilhouette(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  screenCogDeg: number,
  target: TargetState,
  display: DisplayState,
): void {
  const len = 7 + 6 * Math.sqrt(Math.max(0.05, target.rcs));
  const profile = hullProfile(target);
  const beam = len * profile.beamFactor;
  const rad = ((screenCogDeg - 90) * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  ctx.save();
  ctx.fillStyle = profile.fill;
  ctx.strokeStyle = "rgba(255,255,255,0.18)";
  ctx.lineWidth = 0.6;
  ctx.beginPath();
  for (let i = 0; i < profile.pts.length; i++) {
    const pt = profile.pts[i]!;
    const px = pt[0]! * len;
    const py = pt[1]! * (beam / profile.beamFactor); // beam normalised
    const x = cx + px * cos - py * sin;
    const y = cy + px * sin + py * cos;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  // Per-type detail strokes on top of the basic hull. Catamarans get
  // a centreline gap, fishing trawlers a stern gantry, pilot boats a
  // wheelhouse box, etc.
  if (target.vesselType === "fastcat") {
    // Catamaran centreline gap — two parallel lines through the hull.
    ctx.strokeStyle = "rgba(255,255,255,0.4)";
    ctx.lineWidth = 0.6;
    const offsetY = beam * 0.25;
    const tipX = len * 0.45;
    const sternX = -len * 0.4;
    const draw = (oy: number) => {
      const ax = cx + tipX * cos - oy * sin;
      const ay = cy + tipX * sin + oy * cos;
      const bx = cx + sternX * cos - oy * sin;
      const by = cy + sternX * sin + oy * cos;
      ctx.beginPath();
      ctx.moveTo(ax, ay);
      ctx.lineTo(bx, by);
      ctx.stroke();
    };
    draw(-offsetY);
    draw(offsetY);
  }
  // Night lights on top of the silhouette: small red on port,
  // green on starboard, white at stern, white at masthead.
  if (display.nightMode) {
    const lightAt = (alongLen: number, abeam: number, colour: string) => {
      const x = cx + alongLen * cos - abeam * sin;
      const y = cy + alongLen * sin + abeam * cos;
      ctx.shadowColor = colour;
      ctx.shadowBlur = 4;
      ctx.fillStyle = colour;
      ctx.beginPath();
      ctx.arc(x, y, 1.6, 0, Math.PI * 2);
      ctx.fill();
    };
    lightAt(len * 0.2, -beam / 2, "#ef4444"); // port red
    lightAt(len * 0.2, beam / 2, "#22c55e"); // stbd green
    lightAt(-len * 0.4, 0, "#fef3c7"); // stern white
    lightAt(len * 0.4, 0, "#fef3c7"); // masthead white
    ctx.shadowBlur = 0;
  }
  ctx.restore();
}

function drawOwnShip(
  ctx: CanvasRenderingContext2D,
  snapshot: WorldSnapshot,
  proj: Projection,
): void {
  const xy = proj.toXY(snapshot.ownShip.position);
  const screenCog =
    ((snapshot.ownShip.cog - proj.upDeg) % 360 + 360) % 360;
  ctx.save();
  ctx.fillStyle = COLOURS.ownShip;
  paintArrow(ctx, xy.x, xy.y, screenCog, 8);
  ctx.strokeStyle = COLOURS.ownVector;
  ctx.lineWidth = 1.4;
  const len = Math.min(50, snapshot.ownShip.sog * 2.5);
  const rad = ((screenCog - 90) * Math.PI) / 180;
  ctx.beginPath();
  ctx.moveTo(xy.x, xy.y);
  ctx.lineTo(xy.x + Math.cos(rad) * len, xy.y + Math.sin(rad) * len);
  ctx.stroke();
  ctx.restore();
}

function drawSarts(
  ctx: CanvasRenderingContext2D,
  snapshot: WorldSnapshot,
  proj: Projection,
): void {
  ctx.save();
  ctx.strokeStyle = COLOURS.sart;
  ctx.lineWidth = 1.6;
  for (const sart of snapshot.sarts) {
    const xy = proj.toXY(sart.position);
    // Distress X — universal ' MOB ' chart symbol.
    ctx.beginPath();
    ctx.moveTo(xy.x - 5, xy.y - 5);
    ctx.lineTo(xy.x + 5, xy.y + 5);
    ctx.moveTo(xy.x + 5, xy.y - 5);
    ctx.lineTo(xy.x - 5, xy.y + 5);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(xy.x, xy.y, 8, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.restore();
}

/**
 * Paint a small arrow pointing along a true bearing. Used for the own
 * ship and target symbols. (cog - 90) so 0° (north) points up on
 * canvas where the y-axis grows downward.
 */
function paintArrow(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  cogDeg: number,
  size: number,
): void {
  const rad = ((cogDeg - 90) * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  ctx.beginPath();
  ctx.moveTo(x + cos * size, y + sin * size);
  ctx.lineTo(x + Math.cos(rad + (Math.PI * 2) / 3) * size * 0.7, y + Math.sin(rad + (Math.PI * 2) / 3) * size * 0.7);
  ctx.lineTo(x + Math.cos(rad - (Math.PI * 2) / 3) * size * 0.7, y + Math.sin(rad - (Math.PI * 2) / 3) * size * 0.7);
  ctx.closePath();
  ctx.fill();
}

/**
 * Paint a faint range ring around own ship matching the radar's outer
 * range scale, plus a north arrow. The chart is north-up so the arrow
 * is fixed; the ring grows/shrinks with zoom so the student can see
 * directly that "this is the same world-area my radar is showing".
 */
function drawRangeRing(
  ctx: CanvasRenderingContext2D,
  size: { w: number; h: number },
  ownPos: LatLon,
  rangeNm: number,
  proj: Projection,
): void {
  const centre = proj.toXY(ownPos);
  // Pixel radius for `rangeNm` along the latitude axis (1° lat ≈ 60 nm).
  const halfExtentPx = Math.min(size.w, size.h) / 2;
  const rPx = halfExtentPx;
  ctx.save();
  ctx.strokeStyle = "rgba(40, 80, 120, 0.5)";
  ctx.setLineDash([4, 3]);
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.arc(centre.x, centre.y, rPx, 0, Math.PI * 2);
  ctx.stroke();
  // Half-range ring as well, lighter.
  ctx.strokeStyle = "rgba(40, 80, 120, 0.3)";
  ctx.beginPath();
  ctx.arc(centre.x, centre.y, rPx / 2, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
  // Suppress unused-var lint when rangeNm is reported elsewhere.
  void rangeNm;
}

function drawScaleAndLabel(
  ctx: CanvasRenderingContext2D,
  size: { w: number; h: number },
  area: AreaData,
  rangeNm: number,
  orientation: "north-up" | "head-up",
): void {
  ctx.save();
  ctx.fillStyle = "rgba(0, 0, 0, 0.7)";
  ctx.font = "11px ui-sans-serif, system-ui";
  ctx.textBaseline = "top";
  ctx.fillText(
    `Chart · ${area.area} · ${rangeNm} nm · ${orientation === "head-up" ? "head-up" : "north-up"}`,
    8,
    6,
  );

  // North arrow — when head-up, the chart rotates so north no longer
  // points up. We omit the arrow in head-up since "up" is whatever
  // the ship is heading and the orientation is in the label.
  if (orientation === "north-up") {
    const ax = size.w - 22;
    const ay = 22;
    ctx.strokeStyle = "rgba(0, 0, 0, 0.55)";
    ctx.fillStyle = "rgba(0, 0, 0, 0.55)";
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.moveTo(ax, ay - 12);
    ctx.lineTo(ax - 5, ay + 6);
    ctx.lineTo(ax, ay + 2);
    ctx.lineTo(ax + 5, ay + 6);
    ctx.closePath();
    ctx.fill();
    ctx.fillText("N", ax - 4, ay + 8);
  }
  ctx.restore();
}
